import { ReactComponent as InfoIcon } from '@/assets/icon_info.svg';
import React from 'react';
import { FiMessageCircle, FiCopy } from 'react-icons/fi';

import { ProtocoloExecutado } from '@/models/ProtocoloExecutado';

import { useAuth } from '@/features/auth/providers/AuthProvider/hooks';
import PrescricoesButton from '@/features/prescricao-livre/components/PrescricoesButton';
import { useEnterpriseContext } from '@/features/enterprise/contexts/EnterpriseContext';

import ButtonSmall from '@/components/ButtonSmall';
import Tooltip from '@/components/Tooltip';

import PatientSection from '@/features/enterprise/components/DadosAtendimento/PatientSection';
import { usePatientDisplay } from '@/features/enterprise/hooks/useEncounter';
import { useHelpCenterContext } from '@/features/helpcenter/contexts/HelpCenterContext';
import AutoriaProtocolo from '../AutoriaProtocolo';
import {
  Container,
  ExecucaoInfo,
  HeaderActions,
  HeaderColumn,
  HeaderRow,
  HeaderSeparator,
  Info,
} from './styles';

type Props = { protocolo: ProtocoloExecutado };
type DetailsProps = {
  autoria: ProtocoloExecutado['autoria'];
  timeoutInMS?: number;
};

/**
 * TODO: Generalizar este componente
 * Comportamento semelhante ao de SecondaryMenu, com a execção que o dropdown é
 * exibido via hover ao invés de onclick
 */
const AutoriaInfoButton: React.FC<DetailsProps> = ({
  autoria,
  timeoutInMS = 300,
}) => {
  const [menuShow, setMenuShow] = React.useState(false);
  const [displayTimeout, setDisplayTimeout] = React.useState<NodeJS.Timeout>();

  React.useEffect(() => {
    return () => clearTimeout(displayTimeout);
  }, [displayTimeout]);

  return (
    autoria && (
      <Info
        onMouseEnter={() => {
          clearTimeout(displayTimeout);
          setMenuShow(true);
        }}
        onMouseLeave={() => {
          clearTimeout(displayTimeout);
          setDisplayTimeout(setTimeout(() => setMenuShow(false), timeoutInMS));
        }}
      >
        <ButtonSmall>
          <InfoIcon />
        </ButtonSmall>
        {menuShow && <AutoriaProtocolo autoria={autoria} />}
      </Info>
    )
  );
};

const HeaderTitleProtocoloExecutado: React.FC<Props> = ({
  protocolo: { nome, autoria, numero_execucao },
}) => {
  const copyTooltipDefaultTitle = 'Copiar número da execução';
  const copyTooltipSuccessTitle = 'Copiado!';
  const copyFeedbackTimeoutInMS = 2000;

  const { user } = useAuth();
  const [{ mode }] = useEnterpriseContext();
  const {
    actions: { dispatchOpenModal },
  } = useHelpCenterContext();

  const {
    hasMinimalDisplayProperties: canDisplayPatient,
  } = usePatientDisplay();

  const contatoEmail = React.useMemo(() => {
    if (user && user.empresa) {
      return user.empresa.email_contato;
    }

    return null;
  }, [user]);

  const onButtonSupportClick = React.useCallback(() => {
    dispatchOpenModal(0);
  }, [dispatchOpenModal]);

  const [titleCopyButton, setTitleCopyButton] = React.useState(
    copyTooltipDefaultTitle,
  );
  const [isCopyTooltipHovered, setIsCopyTooltipHovered] = React.useState(false);
  const [isCopyFeedbackVisible, setIsCopyFeedbackVisible] = React.useState(
    false,
  );
  const copyFeedbackTimeoutRef = React.useRef<ReturnType<typeof setTimeout>>();

  const clearCopyFeedbackTimeout = React.useCallback(() => {
    clearTimeout(copyFeedbackTimeoutRef.current);
  }, []);

  React.useEffect(() => {
    return clearCopyFeedbackTimeout;
  }, [clearCopyFeedbackTimeout]);

  const changeTitleCopyButton = React.useCallback(() => {
    setTitleCopyButton(copyTooltipSuccessTitle);
    setIsCopyFeedbackVisible(true);
    clearCopyFeedbackTimeout();

    copyFeedbackTimeoutRef.current = setTimeout(() => {
      setTitleCopyButton(copyTooltipDefaultTitle);
      setIsCopyFeedbackVisible(false);
    }, copyFeedbackTimeoutInMS);
  }, [
    clearCopyFeedbackTimeout,
    copyFeedbackTimeoutInMS,
    copyTooltipDefaultTitle,
    copyTooltipSuccessTitle,
  ]);

  const onCopyExecucaoId = React.useCallback(async () => {
    await navigator.clipboard.writeText(String(numero_execucao));
    changeTitleCopyButton();
  }, [numero_execucao, changeTitleCopyButton]);

  const isCopyTooltipVisible = isCopyTooltipHovered || isCopyFeedbackVisible;

  return (
    <Container>
      <HeaderColumn>
        <HeaderRow>
          <h1>{nome}</h1>
          {autoria && <AutoriaInfoButton autoria={autoria} />}
        </HeaderRow>
        <ExecucaoInfo>
          <span>Nº: {numero_execucao}</span>
          <Tooltip
            title={titleCopyButton}
            trigger="manual"
            visible={isCopyTooltipVisible}
          >
            <button
              type="button"
              onClick={onCopyExecucaoId}
              onMouseEnter={() => setIsCopyTooltipHovered(true)}
              onMouseLeave={() => setIsCopyTooltipHovered(false)}
            >
              <FiCopy size={16} />
            </button>
          </Tooltip>
        </ExecucaoInfo>
      </HeaderColumn>

      {canDisplayPatient && (
        <>
          <HeaderSeparator />
          <HeaderColumn>
            <PatientSection mode="clean" />
          </HeaderColumn>
        </>
      )}

      <HeaderActions>
        {mode === 'integrated' && !!contatoEmail && (
          <ButtonSmall onClick={onButtonSupportClick}>
            <FiMessageCircle size={22} />
            Ajuda
          </ButtonSmall>
        )}

        <PrescricoesButton />
      </HeaderActions>
    </Container>
  );
};

export default HeaderTitleProtocoloExecutado;
